import { assertMover, isPassable } from './clearance.js';
import { VEC2_ZERO, add, directionTo, distance, dot, magnitude, magnitudeSquared, moveTowards, scale, subtract, vec2, } from './geometry.js';
import { DEFAULT_MOVE_SPEED, cellCentre, createWalker, stepDistance, stepWalker, } from './movement.js';
import { findPath, hasLineOfSight, smoothPath } from './pathfinding.js';
import { nearestReachable } from './reachable.js';
/**
 * How much of an overlap a body resolves per tick, per neighbour it overlaps.
 *
 * A half each, so a pair pushing against nothing else comes apart in exactly one
 * tick and neither one owns the ground more than the other. A crowd converges
 * over several ticks instead, which is what makes a pack spread into a wall
 * rather than explode off one frame's worth of stacking.
 *
 * A placeholder, like `DEFAULT_MOVE_SPEED` — tuned by feel once monsters are on
 * screen (`TODO.md` #103).
 */
export const SEPARATION_RESPONSE = 0.5;
/**
 * The furthest separation may move a body in one tick, in cells.
 *
 * Pushes from every overlapping neighbour add up, so a pile of monsters on one
 * square would otherwise fling the outermost of them across the room. Half a
 * cell is the cap: it is 10 cells per second at 20 Hz — faster than anything
 * walks, so crowding always wins over walking — and it is small enough that a
 * shove can never carry a body past its immediate neighbour cell, which is what
 * keeps the passability check below honest about the ground being crossed.
 */
export const SEPARATION_MAX_STEP = 0.5;
/**
 * The axis two bodies in *exactly* the same place come apart along.
 *
 * Coincident is an ordinary state — a pack spawned on one cell — and there is no
 * direction between them to push along, so one is chosen: the later-listed body
 * goes this way and the earlier one goes the other. Arbitrary, fixed, and above
 * all not random; a coin flip here would desync client from server.
 */
const COINCIDENT_AXIS = vec2(1, 0);
/** Only monsters push each other out of the way. Players pass through each other. */
const separates = (entity) => entity.kind === 'monster';
/**
 * Whether `body` is something `walker` cannot walk through.
 *
 * Two kinds and one direction: a monster blocks a player, and nothing blocks
 * anything else. Stated as a rule about a *pair* rather than a flag on one of
 * them because that is the shape of the decision — the same monster that stops a
 * player is walked through by another monster, which resolves it by separating
 * instead (`TODO.md` #102 re-opens both predicates the day a third kind exists).
 *
 * An entity is therefore never its own obstacle: nothing obstructs its own kind.
 * The caller still excludes it by id, because that is cheap and this is the one
 * place a self-collision would be a silent, permanent freeze.
 */
const obstructs = (body, walker) => body.kind === 'monster' && walker.kind === 'player';
/** An empty world on this floor. */
export const createWorld = (grid) => ({ grid, entities: [] });
/** The entity with this id, or `null` when the world holds no such entity. Absence is not an error. */
export const entityById = (world, id) => world.entities.find((entity) => entity.id === id) ?? null;
const replace = (world, entity) => ({
    ...world,
    entities: world.entities.map((current) => (current.id === entity.id ? entity : current)),
});
/**
 * Moves an entity, keeping the walker's own copy of the position in step.
 *
 * The duplication is `Walker`'s: it is a self-contained thing that can be
 * stepped on its own. Writing both here, in one place, is what stops the next
 * step resuming from a position the entity has already left.
 */
const placeAt = (entity, position) => ({
    ...entity,
    position,
    walker: entity.walker === null ? null : { ...entity.walker, position },
});
/**
 * The route from where an entity stands to a target, as a walker — or `null`
 * when there is nothing to walk.
 *
 * The target is a wish, not a promise: it goes through `nearestReachable` first,
 * so a click on rock, on a sealed cavern or on a gap this body does not fit
 * through becomes a walk to the closest cell that does admit it (`TODO.md` #92).
 * An entity ordered onto the cell it already occupies is therefore ordered
 * nowhere, and stands still rather than shuffling to the middle of it.
 */
const routeTo = (grid, entity, target) => {
    const destination = nearestReachable(grid, entity.position, target, entity.mover);
    // Both fall out of the same guarantee: a body standing somewhere legal can
    // always reach at least its own cell, and whatever `nearestReachable` picked
    // it reached by the movement rule A* is about to search with.
    const route = destination === null ? null : findPath(grid, entity.position, destination, entity.mover);
    if (route === null)
        return null;
    const walker = createWalker(entity.position, smoothPath(grid, route, entity.mover), entity.speed);
    return walker.path.length === 0 ? null : walker;
};
/**
 * Puts a body on a cell, in the middle of it.
 *
 * The guarded placement the whole "no stuck movers" argument rests on: a cell
 * whose clearance does not admit this body is refused outright, and there is no
 * nudge, no search for somewhere nearby and no push-out — the caller decides
 * what to do about a spawn point that does not fit, because only the caller
 * knows whether that is a pack member to drop or a level to reject.
 *
 * Nothing is said about a cell another body is already standing on: bodies share
 * ground, and separation is what spreads a pack spawned on one square.
 *
 * A mover built for a different map, or a nonsense radius, throws — the same
 * line `findPath` draws between a bad question and a bad program. A speed that
 * is not a number is `movement.ts`'s rule to state, and it does, the moment the
 * entity is first ordered to walk.
 */
export const spawn = (world, request) => {
    assertMover(world.grid, request.mover);
    // Before anything geometric: a duplicate id means the caller has lost track of
    // what it has placed, and answering "no room" would send them to the map.
    if (world.entities.some((current) => current.id === request.id)) {
        return { ok: false, reason: 'duplicate-id' };
    }
    const position = cellCentre(request.cell);
    if (!isPassable(world.grid, position, request.mover))
        return { ok: false, reason: 'no-room' };
    const entity = {
        id: request.id,
        kind: request.kind,
        position,
        speed: request.speed ?? DEFAULT_MOVE_SPEED,
        mover: request.mover,
        walker: null,
    };
    return { ok: true, world: { ...world, entities: [...world.entities, entity] }, entity };
};
/**
 * Takes a body out of the world — what `spawn` undoes.
 *
 * Placement is guarded and removal is not: there is no cell to admit, no body to
 * measure and nothing to refuse but the id itself. What it is here for is that
 * *every* change to the entity list is made by the module that owns the list —
 * the room removing a player who left, and, from M3, whatever kills a monster
 * (`TODO.md` #132). One definition, so the day an entity is more than an entry
 * in an array there is one place to say so.
 *
 * ## The contract
 *
 *   - **A world comes back, not a mutation.** Worlds are values here like
 *     everywhere else in core: the world handed in is untouched, and the
 *     survivors come back as the very same objects, in the same order — which
 *     matters, because that order is what breaks the coincident-bodies tie
 *     (`TODO.md` #106).
 *   - **Removing an entity that is not there is a refusal, not a no-op.** It is
 *     the mirror of `spawn`'s `duplicate-id`: an id the world does not hold
 *     means the caller has lost track of what it placed, and answering "done"
 *     would hide that. A caller for whom absence is genuinely ordinary — a room
 *     letting the same seat go twice — ignores the failure, but has to say so.
 *   - **Nothing is left referencing the body.** Nothing in a `World` names an
 *     entity: a walker holds waypoints, which are points on the grid, and
 *     separation and blocking are recomputed every tick from the list they are
 *     handed. So the pack that was pushing on it simply stops finding it, the
 *     player it was blocking walks on the next tick, and neither needs repairing
 *     — a route is planned over terrain, not over occupancy (`TODO.md` #141).
 *     The route of the entity that left goes with it, being a field of it.
 */
export const despawn = (world, id) => {
    const entity = entityById(world, id);
    if (entity === null)
        return { ok: false, reason: 'not-found' };
    return {
        ok: true,
        world: { ...world, entities: world.entities.filter((current) => current.id !== id) },
        entity,
    };
};
/**
 * Sends an entity to a target, replacing whatever it was doing.
 *
 * This is the click, and the AI's "chase that": one call, from wherever the
 * entity currently is, at any point in a walk. The new route starts at the
 * entity's continuous position, so re-pathing mid-stride neither teleports it
 * back to a cell centre nor loses the ground it has covered.
 *
 * Ordering an entity to the cell it already stands in stops it, which is the
 * same rule as everything else here: there is nothing to walk, so it walks
 * nothing.
 */
export const orderMove = (world, id, target) => {
    const entity = entityById(world, id);
    if (entity === null)
        return { ok: false, reason: 'not-found' };
    // `spawn` makes this impossible and the walkable world never shrinks, so this
    // is not a recovery path — it is the refusal that keeps a hand-built or
    // wire-restored state from being answered with a crash.
    if (!isPassable(world.grid, entity.position, entity.mover)) {
        return { ok: false, reason: 'unreachable' };
    }
    const ordered = { ...entity, walker: routeTo(world.grid, entity, target) };
    return { ok: true, world: replace(world, ordered), entity: ordered };
};
/**
 * How far a body may travel along the unit vector `heading` before it touches
 * `obstacle` — `Infinity` when this heading never brings the two together.
 *
 * Bodies are discs, so "touching" is `|offset + t·heading| = r₁ + r₂`: a
 * quadratic in `t` whose leading coefficient is exactly 1 because the heading is
 * a unit vector, leaving `t² + 2·approach·t + gap = 0`. Its nearer root is where
 * the moving body's edge first meets the other's, which is the distance being
 * asked for.
 *
 * Solved, not sampled. That is why no speed tunnels one body through another: a
 * step long enough to cross the far side still meets the near face first, and it
 * is the near face this returns.
 */
const travelUntilContact = (entity, heading, obstacle) => {
    const offset = subtract(entity.position, obstacle.position);
    const touching = entity.mover.radius + obstacle.mover.radius;
    const approach = dot(offset, heading);
    const gap = magnitudeSquared(offset) - touching * touching;
    // Already touching, or already inside. There is no contact ahead to stop short
    // of, so the only thing refused is going deeper — a heading that does not
    // close the gap is free, and one that does buys nothing and is spent standing.
    if (gap <= 0)
        return approach >= 0 ? Number.POSITIVE_INFINITY : 0;
    const discriminant = approach * approach - gap;
    // The line misses this body altogether.
    if (discriminant < 0)
        return Number.POSITIVE_INFINITY;
    const contact = -approach - Math.sqrt(discriminant);
    // Both roots behind: outside this body, and walking away from it.
    return contact < 0 ? Number.POSITIVE_INFINITY : contact;
};
/**
 * The furthest this entity may walk towards `heading` this tick, given every
 * body in the world — `Infinity` when nothing stands in its way.
 *
 * Measured from the pre-pass positions the caller hands in, so the answer cannot
 * depend on which entities have already been stepped.
 */
const freeTravel = (entity, heading, bodies) => {
    const direction = directionTo(entity.position, heading);
    let limit = Number.POSITIVE_INFINITY;
    for (const body of bodies) {
        if (body.id === entity.id || !obstructs(body, entity))
            continue;
        limit = Math.min(limit, travelUntilContact(entity, direction, body));
    }
    return limit;
};
/** One tick along a route, cut short by whatever is standing in the way of it. */
const stepRoute = (entity, route, bodies) => {
    const heading = route.path[0];
    // Nothing left to walk: `stepWalker` hands the walker straight back, and the
    // caller reads the spent route as a standing entity.
    if (heading === undefined)
        return stepWalker(route).walker;
    const limit = freeTravel(entity, heading, bodies);
    // The common case, and the only case before this rule existed: the whole step
    // is spent on the route, corners, waypoints and unspent remainders included.
    if (limit >= stepDistance(route.speed))
        return stepWalker(route).walker;
    // Blocked. The same route and the same heading, advanced by the ground that is
    // free. No waypoint is consumed: `moveTowards` cannot carry a body past the
    // point it is aiming at, so the worst a cut step does is land exactly on the
    // next waypoint, and the following tick consumes that one as it always would.
    return { ...route, position: moveTowards(entity.position, heading, limit) };
};
/** One tick of walking, cut short by any body in the way, and the route cleared when it runs out. */
const walk = (entity, bodies) => {
    if (entity.walker === null)
        return entity;
    const walker = stepRoute(entity, entity.walker, bodies);
    return {
        ...entity,
        position: walker.position,
        // An empty route is a standing entity. Clearing it here means "is it
        // walking" is one question with one answer everywhere downstream.
        walker: walker.path.length === 0 ? null : walker,
    };
};
/**
 * How far each body is pushed out of the ones it is standing inside, this tick.
 *
 * Every push is computed from the positions as they stand at the start of the
 * pass and applied afterwards, so no body sees a neighbour that has already
 * moved and the result does not depend on the order pairs are visited in. The
 * *list* order still decides the coincident case, and only that.
 */
const separationPushes = (entities) => {
    const pushes = new Array(entities.length).fill(VEC2_ZERO);
    for (const [index, body] of entities.entries()) {
        if (!separates(body))
            continue;
        for (let other = index + 1; other < entities.length; other += 1) {
            const partner = entities[other];
            // `noUncheckedIndexedAccess` types the read as optional; `other` is bounded
            // by the array's own length, so the guard is unreachable.
            if (partner === undefined || !separates(partner))
                continue;
            const gap = distance(body.position, partner.position);
            const overlap = body.mover.radius + partner.mover.radius - gap;
            if (overlap <= 0)
                continue;
            const axis = gap === 0 ? COINCIDENT_AXIS : directionTo(body.position, partner.position);
            const push = scale(axis, SEPARATION_RESPONSE * overlap);
            pushes[index] = add(pushes[index] ?? VEC2_ZERO, scale(push, -1));
            pushes[other] = add(pushes[other] ?? VEC2_ZERO, push);
        }
    }
    return pushes;
};
/** The same push, held to what one tick of crowding is allowed to move a body. */
const capped = (push) => {
    const length = magnitude(push);
    return length <= SEPARATION_MAX_STEP ? push : scale(push, SEPARATION_MAX_STEP / length);
};
/**
 * Applies one body's push, and repairs the route if the push invalidated it.
 *
 * A shove may only put a body where it could have walked: the destination is
 * checked with the movement rule itself — line of sight under this body's own
 * mover, which is passability at both ends plus the corner rule between them —
 * and a push that fails it is simply not applied. That is what keeps separation
 * from doing the push-out this project rejected, and what makes "terrain is
 * clearance's business" true rather than merely intended.
 *
 * A body that did move may have left the cell its route was built from, so the
 * first leg is re-checked against the same rule. Only a leg that no longer holds
 * costs a rebuild, so the common case — a nudge inside the same corridor — pays
 * for a short line trace and nothing else.
 */
const shove = (grid, entity, push) => {
    if (push.x === 0 && push.y === 0)
        return entity;
    const pushed = add(entity.position, capped(push));
    if (!hasLineOfSight(grid, entity.position, pushed, entity.mover))
        return entity;
    const moved = placeAt(entity, pushed);
    const waypoint = moved.walker?.path[0];
    if (waypoint === undefined)
        return moved;
    if (hasLineOfSight(grid, moved.position, waypoint, moved.mover))
        return moved;
    const destination = moved.walker?.path[moved.walker.path.length - 1] ?? waypoint;
    return { ...moved, walker: routeTo(grid, moved, destination) };
};
/**
 * Advances the whole world by exactly one fixed tick.
 *
 * Pure: the world handed in is never touched, and an entity with nothing to do
 * comes back as the very same object. Deterministic: no clock, no randomness, no
 * iteration over anything unordered — the same world stepped twice is the same
 * world, bit for bit, on the server and on every client predicting it.
 */
export const stepWorld = (world) => {
    // Every walk is cleared against the world as it stood when the tick began, so
    // no entity is blocked by a body that has already moved this tick.
    const walked = world.entities.map((entity) => walk(entity, world.entities));
    const pushes = separationPushes(walked);
    return {
        ...world,
        entities: walked.map((entity, index) => shove(world.grid, entity, pushes[index] ?? VEC2_ZERO)),
    };
};
//# sourceMappingURL=world.js.map